import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["button", "spinner", "text"]

  connect() {
    this.isLoading = false
  }

  // Toggle loading state on button
  toggleLoading(event) {
    if (event) event.preventDefault()
    
    this.isLoading = !this.isLoading
    this.updateButton()
  }

  // Start loading state
  startLoading(event) {
    if (event) event.preventDefault()
    
    this.isLoading = true
    this.updateButton()
  }

  // Stop loading state
  stopLoading(event) {
    if (event) event.preventDefault()
    
    this.isLoading = false
    this.updateButton()
  }

  // Simulate async action with loading state
  async simulateAsync(event) {
    if (event) event.preventDefault()
    
    const button = event.currentTarget
    const originalText = button.textContent.trim()
    
    // Start loading
    this.isLoading = true
    this.updateButtonElement(button, true, originalText)
    
    // Simulate async operation (2 seconds)
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    // Stop loading
    this.isLoading = false
    this.updateButtonElement(button, false, originalText)
  }

  // Update button state
  updateButton() {
    this.buttonTargets.forEach(button => {
      const originalText = button.dataset.originalText || button.textContent.trim()
      button.dataset.originalText = originalText
      this.updateButtonElement(button, this.isLoading, originalText)
    })
  }

  // Update individual button element
  updateButtonElement(button, loading, originalText) {
    if (loading) {
      button.disabled = true
      
      // Check if spinner already exists
      if (!button.querySelector('.spinner-wrapper')) {
        const spinner = this.createSpinner()
        button.insertBefore(spinner, button.firstChild)
      }
      
      // Update text if text target exists
      if (this.hasTextTarget) {
        this.textTarget.textContent = 'Loading...'
      }
    } else {
      button.disabled = false
      
      // Remove spinner
      const spinner = button.querySelector('.spinner-wrapper')
      if (spinner) {
        spinner.remove()
      }
      
      // Restore original text
      if (this.hasTextTarget) {
        this.textTarget.textContent = originalText
      }
    }
  }

  // Create spinner element
  createSpinner() {
    const wrapper = document.createElement('span')
    wrapper.className = 'spinner-wrapper animate-spin'
    
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('width', '24')
    svg.setAttribute('height', '24')
    svg.setAttribute('viewBox', '0 0 24 24')
    svg.setAttribute('fill', 'none')
    
    const mask = document.createElementNS('http://www.w3.org/2000/svg', 'mask')
    mask.setAttribute('id', `spinner-mask-${Date.now()}`)
    mask.setAttribute('fill', 'white')
    
    const maskPath = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    maskPath.setAttribute('d', 'M15.328 23.5293C17.8047 22.8144 19.9853 21.321 21.547 19.2701C23.1087 17.2193 23.9686 14.72 23.9992 12.1424C24.0297 9.56481 23.2295 7.04587 21.7169 4.95853C20.2043 2.8712 18.0597 1.32643 15.6007 0.552947C13.1417 -0.220538 10.499 -0.181621 8.0638 0.663935C5.62864 1.50949 3.53049 3.11674 2.07999 5.24771C0.629495 7.37868 -0.096238 9.92009 0.0102418 12.4957C0.116722 15.0713 1.04975 17.5441 2.6712 19.5481L4.96712 17.6904C3.74474 16.1796 3.04133 14.3154 2.96106 12.3737C2.88079 10.432 3.42791 8.51604 4.52142 6.90953C5.61493 5.30301 7.19671 4.09133 9.03255 3.45387C10.8684 2.81642 12.8607 2.78708 14.7145 3.3702C16.5683 3.95332 18.1851 5.1179 19.3254 6.69152C20.4658 8.26514 21.0691 10.1641 21.046 12.1074C21.023 14.0506 20.3748 15.9347 19.1974 17.4809C18.02 19.027 16.3761 20.1528 14.5089 20.6918L15.328 23.5293Z')
    
    mask.appendChild(maskPath)
    
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('d', 'M15.328 23.5293C17.8047 22.8144 19.9853 21.321 21.547 19.2701C23.1087 17.2193 23.9686 14.72 23.9992 12.1424C24.0297 9.56481 23.2295 7.04587 21.7169 4.95853C20.2043 2.8712 18.0597 1.32643 15.6007 0.552947C13.1417 -0.220538 10.499 -0.181621 8.0638 0.663935C5.62864 1.50949 3.53049 3.11674 2.07999 5.24771C0.629495 7.37868 -0.096238 9.92009 0.0102418 12.4957C0.116722 15.0713 1.04975 17.5441 2.6712 19.5481L4.96712 17.6904C3.74474 16.1796 3.04133 14.3154 2.96106 12.3737C2.88079 10.432 3.42791 8.51604 4.52142 6.90953C5.61493 5.30301 7.19671 4.09133 9.03255 3.45387C10.8684 2.81642 12.8607 2.78708 14.7145 3.3702C16.5683 3.95332 18.1851 5.1179 19.3254 6.69152C20.4658 8.26514 21.0691 10.1641 21.046 12.1074C21.023 14.0506 20.3748 15.9347 19.1974 17.4809C18.02 19.027 16.3761 20.1528 14.5089 20.6918L15.328 23.5293Z')
    path.setAttribute('stroke', 'white')
    path.setAttribute('stroke-width', '14')
    path.setAttribute('mask', `url(#${mask.id})`)
    
    svg.appendChild(mask)
    svg.appendChild(path)
    wrapper.appendChild(svg)
    
    return wrapper
  }
}
